import { useState, useCallback, useRef, useEffect } from 'react';
import { Dropdown } from '@/components/Dropdown';
import PNV from '@/components/PositiveNegativeValue';
import TopWorst, { Period } from '../TopWorst';
import { ORDERED_MARKETS, PRIMARY_MARKETS } from '@/constants/markets';
import type { PrimaryCountryISO } from '@/constants/countries';
import { TickerSymbol, Name, Company, Industry } from './styled';
import track, { useTracking } from 'react-tracking';
import { CellLink } from '../DataTable';
import If from '@/components/If';
import { Bones } from '@simplywallst/ui-core';
import { useQuery } from '@tanstack/react-query';
import useUser from '@/hooks/useUser';
import queryMarket, { getDefault as getDefaultMarket } from './queryMarket';
import queryIndustry, {
  getDefault as getDefaultIndustry,
} from './queryIndustry';
import { push } from 'redux-first-router';
import useIndustryOptions from '@/pages/Market/hooks/useIndustryOptions';
import { find } from 'lodash';
import { Sentry } from '@/components/Sentry';
import { useReduxForDashboard } from '../../hooks/useReduxForDashboard';
import { persistedQueryConfig } from '@/utilities/persistQueryClient/utilities';

enum CompanyTabs {
  Top = 'Top Gainers',
  Worst = 'Top Losers',
}

enum IndustryTabs {
  Top = 'Top Industries',
  Worst = 'Worst Industries',
}

interface Entity {
  return7D: number | null;
  return7DFormatted: string;
  return30D: number | null;
  return30DFormatted: string;
  return90D: number | null;
  return90DFormatted: string;
  return1YrAbs: number | null;
  return1YrAbsFormatted: string;
  return3YrAbs: number | null;
  return3YrAbsFormatted: string;
  return5YrAbs: number | null;
  return5YrAbsFormatted: string;
}

export interface CompanyEntity extends Entity {
  name: string;
  canonicalUrl: string;
  tickerSymbol: string;
  uniqueSymbol: string;
}

export interface IndustryEntity extends Entity {
  name: string;
  canonicalUrl: string;
}

const selectReturnValue = (entity: Entity, period: Period) => {
  switch (period) {
    case '7D':
      return entity.return7D;
    case '1M':
      return entity.return30D;
    case '3M':
      return entity.return90D;
    case '1Y':
      return entity.return1YrAbs;
    case '3Y':
      return entity.return3YrAbs;
    case '5Y':
      return entity.return5YrAbs;
    default:
      return null;
  }
};

const selectReturnValueFormatted = (entity: Entity, period: Period) => {
  switch (period) {
    case '7D':
      return entity.return7DFormatted;
    case '1M':
      return entity.return30DFormatted;
    case '3M':
      return entity.return90DFormatted;
    case '1Y':
      return entity.return1YrAbsFormatted;
    case '3Y':
      return entity.return3YrAbsFormatted;
    case '5Y':
      return entity.return5YrAbsFormatted;
    default:
      return null;
  }
};

const MarketTopWorst = () => {
  const { navigateToScreener } = useReduxForDashboard();
  const { trackEvent } = useTracking();
  const { data: userData, isFetched: userIsFetched } = useUser();

  const { current: marketOptions } = useRef(
    ORDERED_MARKETS.map((market) => ({
      option: market,
      label: PRIMARY_MARKETS[market].label,
    }))
  );

  const [selectedMarket, setSelectedMarket] = useState<PrimaryCountryISO>();

  const enabled = typeof selectedMarket !== 'undefined' && userIsFetched;

  const { data: marketData = getDefaultMarket(), status: marketFetchStatus } =
    useQuery({
      queryKey: ['dashboard-market-performance', selectedMarket || 'us'],
      queryFn: queryMarket,
      enabled,
      ...persistedQueryConfig,
    });

  const {
    data: industryData = getDefaultIndustry(),
    status: industryFetchStatus,
  } = useQuery({
    queryKey: ['dashboard-industry-performance', selectedMarket || 'us'],
    queryFn: queryIndustry,
    enabled,
    ...persistedQueryConfig,
  });

  useEffect(() => {
    if (typeof selectedMarket === 'undefined' && userIsFetched) {
      setSelectedMarket(
        userData.defaultMarketISOWithUsFallback as PrimaryCountryISO
      );
    }
  }, [selectedMarket, userData, userIsFetched]);

  const handleSelect = useCallback((value) => {
    setSelectedMarket(value);
  }, []);

  const { options } = useIndustryOptions(false, selectedMarket);
  const getMarketPageIndustryUrl = useCallback(
    (market, industryId) => {
      const item = find(options, { id: industryId });
      return item ? item.href : '';
    },
    [options]
  );

  return (
    <div data-cy-id="dashboard-top-worst">
      <Dropdown
        canSearch={true}
        name="marketDropdown"
        options={marketOptions}
        value={selectedMarket || 'us'}
        placeholder="Select a market"
        onSelect={handleSelect}
      />
      <Sentry
        sentryCapture={{
          team: 'dingoes',
          extra: {
            page: 'MarketTopWorst',
            marketData,
            industryData,
            marketFetchStatus,
            industryFetchStatus,
          },
        }}
        customCopy={<span>Something went wrong. Please try again.</span>}
        globalDisabled
        isShownLogo={false}
        isShownHeading={false}
      >
        <TopWorst
          topLabel={CompanyTabs.Top}
          worstLabel={CompanyTabs.Worst}
          disabled={marketFetchStatus === 'pending'}
        >
          {({ period, tab }) =>
            marketData &&
            marketData[tab] &&
            marketData[tab][period] &&
            marketData[tab][period].map((n, i) => (
              <tr
                key={`top-worst-${n.id}-${period}`}
                title={n.name}
                data-cy-id="dashboard-top-worst-company-item"
              >
                <Company>
                  <CellLink
                    to={n.canonicalUrl}
                    onClick={() =>
                      trackEvent({
                        action: 'click',
                        modifier: 'company_list_item',
                        companyId: n,
                        uniqueSymbol: n.uniqueSymbol,
                        index: i,
                      })
                    }
                  >
                    <TickerSymbol data-cy-id="dashboard-top-worst-ticker">{`${n.tickerSymbol} `}</TickerSymbol>
                    <Name data-cy-id="dashboard-top-worst-name">{n.name}</Name>
                  </CellLink>
                </Company>
                <PNV
                  as="td"
                  value={selectReturnValue(n, period)}
                  data-cy-id="dashboard-top-worst-value"
                >
                  <CellLink
                    to={n.canonicalUrl}
                    onClick={() =>
                      trackEvent({
                        action: 'click',
                        modifier: 'company_list_item',
                        companyId: n,
                        uniqueSymbol: n.uniqueSymbol,
                        index: i,
                      })
                    }
                  >
                    {selectReturnValueFormatted(n, period)}
                  </CellLink>
                </PNV>
              </tr>
            ))
          }
        </TopWorst>
        <TopWorst
          topLabel={IndustryTabs.Top}
          worstLabel={IndustryTabs.Worst}
          disabled={industryFetchStatus === 'pending'}
        >
          {({ period, tab }) =>
            industryData &&
            industryData[tab] &&
            industryData[tab][period] &&
            industryData[tab][period].map((n, i) => {
              const marketIndustryUrl = getMarketPageIndustryUrl(
                selectedMarket,
                n.id.toString()
              );
              return (
                <tr
                  key={`top-worst-${n.id}-${period}`}
                  title={n.name}
                  data-cy-id="dashboard-top-worst-industry-item"
                  onClick={() => {
                    marketIndustryUrl
                      ? push(marketIndustryUrl)
                      : navigateToScreener(selectedMarket, n.slug);
                    trackEvent({
                      action: 'click',
                      modifier: 'industry_list_item',
                      industryId: n.id,
                      industryName: n.name,
                      index: i,
                    });
                  }}
                >
                  <Industry>
                    <If match={n.name.length > 0}>
                      <span data-cy-id="dashboard-top-worst-name">
                        {n.name}
                      </span>
                    </If>
                    <If match={n.name.length === 0}>
                      <Bones />
                    </If>
                  </Industry>
                  <PNV
                    as="td"
                    value={selectReturnValue(n, period)}
                    data-cy-id="dashboard-top-worst-value"
                  >
                    <span>{selectReturnValueFormatted(n, period)}</span>
                  </PNV>
                </tr>
              );
            })
          }
        </TopWorst>
      </Sentry>
    </div>
  );
};

export default track({
  type: 'track',
  subject: 'market_top_worst_module',
})(MarketTopWorst) as typeof MarketTopWorst;
